import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FmdGoodRoundedIcon from '@mui/icons-material/FmdGoodRounded';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { onAuthStateChanged, User } from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFirebase } from '../../services/context/authContext';
import {
    clearCheckoutAttemptMarker,
    getPublicEvent,
    getPurchasedTickets,
    readCheckoutAttemptMarker,
} from '../../services/publicCheckout';
import { EventData, PurchasedTicketData } from '../../util/types';
import { checkoutPalette } from './checkoutTheme';

const formatEventDate = (value?: string) => {
    if (!value) {
        return null;
    }

    const date = dayjs(value);
    if (!date.isValid()) {
        return null;
    }

    return date.format('D. MMM YYYY [um] HH:mm');
};

const getVenueLine = (event: EventData | null) => {
    if (!event) {
        return null;
    }

    return event.eventVenueName || event.eventAddress || null;
};

const getPurchasedAtMs = (ticket: PurchasedTicketData) => {
    const purchasedAt = Date.parse(ticket.purchasedDate);
    return Number.isNaN(purchasedAt) ? 0 : purchasedAt;
};

const resolveTicketPdfUrl = ({
    eventId,
    tickets,
    userId,
}: {
    eventId: string;
    tickets: PurchasedTicketData[];
    userId: string;
}) => {
    const checkoutAttempt = readCheckoutAttemptMarker();
    const matchingTickets = tickets
        .filter((ticket) => ticket.userId === userId)
        .filter((ticket) => ticket.eventId === eventId)
        .filter((ticket) => Boolean(ticket.pdfUrl))
        .sort((left, right) => getPurchasedAtMs(right) - getPurchasedAtMs(left));

    if (matchingTickets.length === 0) {
        return null;
    }

    if (
        checkoutAttempt
        && checkoutAttempt.eventId === eventId
        && checkoutAttempt.userId === userId
    ) {
        const recentTickets = matchingTickets.filter(
            (ticket) => getPurchasedAtMs(ticket) >= checkoutAttempt.startedAt - (5 * 60 * 1000)
        );

        if (recentTickets.length > 0) {
            const preferredTransactionId = recentTickets[0].transactionId;
            if (preferredTransactionId) {
                const sameTransactionTicket = recentTickets.find(
                    (ticket) => ticket.transactionId === preferredTransactionId && ticket.pdfUrl
                );
                if (sameTransactionTicket?.pdfUrl) {
                    return sameTransactionTicket.pdfUrl;
                }
            }

            if (recentTickets[0].pdfUrl) {
                return recentTickets[0].pdfUrl;
            }
        }
    }

    return matchingTickets[0].pdfUrl ?? null;
};

export default function PublicEventCheckoutSuccessPage() {
    const { eventId = '' } = useParams();
    const [searchParams] = useSearchParams();
    const { auth } = useFirebase();
    const redirectStatus = searchParams.get('redirect_status');
    const [event, setEvent] = useState<EventData | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<User | null>(auth.currentUser);
    const [loadingEvent, setLoadingEvent] = useState(true);
    const [ticketPdfUrl, setTicketPdfUrl] = useState<string | null>(null);
    const [loadingTicketPdf, setLoadingTicketPdf] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user);
        });

        return unsubscribe;
    }, [auth]);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const nextEvent = await getPublicEvent(eventId);
                if (active) {
                    setEvent(nextEvent);
                }
            } catch {
                if (active) {
                    setEvent(null);
                }
            } finally {
                if (active) {
                    setLoadingEvent(false);
                }
            }
        };

        if (eventId) {
            load();
            return () => {
                active = false;
            };
        }

        setLoadingEvent(false);
        return () => {
            active = false;
        };
    }, [eventId]);

    const statusMessage = useMemo(() => {
        if (redirectStatus === 'processing') {
            return {
                title: 'Zahlung wird verarbeitet',
                body: 'Stripe finalisiert diese Zahlungsmethode noch. Deine Tickets werden danach demselben Konto zugeordnet.',
            };
        }

        if (redirectStatus === 'failed') {
            return {
                title: 'Zahlung fehlgeschlagen',
                body: 'Der Redirect-Flow wurde nicht erfolgreich abgeschlossen. Du kannst zur Eventseite zurueckkehren und es erneut versuchen.',
            };
        }

        return {
            title: 'Du bist dabei!',
            body: 'Dein Ticket wurde an deine E-Mail gesendet.',
        };
    }, [redirectStatus]);

    const backgroundImage = event?.eventImage;
    const eventDateLine = formatEventDate(event?.startTime);
    const venueLine = getVenueLine(event);
    const isSuccess = !redirectStatus || redirectStatus === 'succeeded';

    useEffect(() => {
        if (!isSuccess || !eventId || !firebaseUser) {
            setLoadingTicketPdf(false);
            return;
        }

        let cancelled = false;
        const maxAttempts = redirectStatus === 'processing' ? 20 : 8;

        const loadTicketPdfUrl = async () => {
            setLoadingTicketPdf(true);

            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                try {
                    const token = await firebaseUser.getIdToken();
                    const ticketResponse = await getPurchasedTickets(firebaseUser.uid, token, 30);
                    const nextPdfUrl = resolveTicketPdfUrl({
                        eventId,
                        tickets: ticketResponse.tickets ?? [],
                        userId: firebaseUser.uid,
                    });

                    if (nextPdfUrl) {
                        if (!cancelled) {
                            setTicketPdfUrl(nextPdfUrl);
                            setLoadingTicketPdf(false);
                            clearCheckoutAttemptMarker();
                        }
                        return;
                    }
                } catch {
                    // Keep retrying briefly; webhook fulfillment may still be settling.
                }

                if (attempt < maxAttempts - 1) {
                    await new Promise((resolve) => window.setTimeout(resolve, 1500));
                }
            }

            if (!cancelled) {
                setLoadingTicketPdf(false);
            }
        };

        loadTicketPdfUrl();

        return () => {
            cancelled = true;
        };
    }, [eventId, firebaseUser, isSuccess, redirectStatus]);

    const secondaryButtonLabel = ticketPdfUrl
        ? 'Ticket PDF öffnen'
        : (loadingTicketPdf && isSuccess ? 'Ticket PDF wird vorbereitet' : 'Ticket wurde an deine E-Mail gesendet');
    const primaryButtonSx = {
        borderRadius: 2.5,
        py: 1.2,
        textTransform: 'none',
        fontWeight: 800,
        color: checkoutPalette.secondary,
        bgcolor: 'rgba(255,255,255,0.18)',
        backdropFilter: 'blur(10px)',
        boxShadow: 'none',
        '&:hover': {
            bgcolor: 'rgba(255,255,255,0.24)',
            boxShadow: 'none',
        },
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: checkoutPalette.primary,
                color: checkoutPalette.secondary,
            }}
        >
            {backgroundImage ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'none',
                        transform: 'scale(1.02)',
                    }}
                />
            ) : null}

            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `
                        linear-gradient(180deg, rgba(6,8,9,0.08) 0%, rgba(6,8,9,0.22) 22%, rgba(6,8,9,0.48) 56%, rgba(6,8,9,0.88) 82%, rgba(6,8,9,0.98) 100%),
                        radial-gradient(circle at top, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 42%)
                    `,
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    px: { xs: 2.25, sm: 3.5 },
                    py: { xs: 3, sm: 4 },
                }}
            >
                <Box />

                <Stack
                    spacing={2.2}
                    alignItems="center"
                    textAlign="center"
                    sx={{
                        width: '100%',
                        maxWidth: 560,
                        mx: 'auto',
                    }}
                >
                    {loadingEvent ? (
                        <CircularProgress sx={{ color: checkoutPalette.secondary }} />
                    ) : (
                        <>
                            <Box
                                sx={{
                                    width: 88,
                                    height: 88,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    color: checkoutPalette.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 18px 40px rgba(0,0,0,0.28)',
                                }}
                            >
                                <CheckRoundedIcon sx={{ fontSize: 50 }} />
                            </Box>

                            <Stack spacing={1} alignItems="center">
                                <Typography
                                    sx={{
                                        fontSize: { xs: '2.35rem', sm: '2.8rem' },
                                        lineHeight: 1.02,
                                        fontWeight: 900,
                                        letterSpacing: '-0.04em',
                                    }}
                                >
                                    {statusMessage.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '1.6rem', sm: '1.95rem' },
                                        lineHeight: 1.08,
                                        fontWeight: 700,
                                        maxWidth: 520,
                                    }}
                                >
                                    {event?.eventName || 'Dein Event'}
                                </Typography>
                            </Stack>

                            <Stack spacing={0.85} alignItems="center">
                                {eventDateLine ? (
                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                        <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.86)' }} />
                                        <Typography color="rgba(255,255,255,0.9)">
                                            {eventDateLine}
                                        </Typography>
                                    </Stack>
                                ) : null}

                                {venueLine ? (
                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                        <FmdGoodRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.86)' }} />
                                        <Typography color="rgba(255,255,255,0.84)">
                                            {venueLine}
                                        </Typography>
                                    </Stack>
                                ) : null}
                            </Stack>

                            {!isSuccess ? (
                                <Typography
                                    sx={{
                                        px: 1.5,
                                        py: 0.7,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(255,255,255,0.12)',
                                        color: checkoutPalette.secondary,
                                        backdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        fontWeight: 700,
                                    }}
                                >
                                    {redirectStatus === 'processing' ? 'Wird verarbeitet' : 'Erneut versuchen'}
                                </Typography>
                            ) : null}

                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.82)',
                                    maxWidth: 520,
                                    fontSize: '1rem',
                                    lineHeight: 1.5,
                                }}
                            >
                                {statusMessage.body}
                            </Typography>
                        </>
                    )}
                </Stack>

                <Stack
                    spacing={1.35}
                    sx={{
                        width: '100%',
                        maxWidth: 560,
                        mx: 'auto',
                        pt: 3,
                    }}
                >
                    <Button
                        variant="contained"
                        href={`/events/${eventId}`}
                        startIcon={<DownloadRoundedIcon />}
                        sx={primaryButtonSx}
                    >
                        Zur Eventseite zurück
                    </Button>

                    {loadingTicketPdf && isSuccess ? (
                        <Button
                            variant="contained"
                            disabled
                            endIcon={<CircularProgress size={16} color="inherit" />}
                            sx={{
                                borderRadius: 2.5,
                                py: 1.35,
                                textTransform: 'none',
                                fontWeight: 900,
                                fontSize: '1.02rem',
                                color: checkoutPalette.primary,
                                bgcolor: checkoutPalette.secondary,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {secondaryButtonLabel}
                        </Button>
                    ) : ticketPdfUrl ? (
                        <Button
                            variant="contained"
                            href={ticketPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<DownloadRoundedIcon />}
                            sx={{
                                borderRadius: 2.5,
                                py: 1.35,
                                textTransform: 'none',
                                fontWeight: 900,
                                fontSize: '1.02rem',
                                color: checkoutPalette.primary,
                                bgcolor: checkoutPalette.secondary,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.92)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {secondaryButtonLabel}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled
                            sx={{
                                borderRadius: 2.5,
                                py: 1.35,
                                textTransform: 'none',
                                fontWeight: 900,
                                fontSize: '1.02rem',
                                color: checkoutPalette.primary,
                                bgcolor: checkoutPalette.secondary,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: checkoutPalette.secondary,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {secondaryButtonLabel}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}
